import { useState } from "react";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IPersonalSiteFilter, ITableColumn, IWorkspaceFilter } from "@interfaces/common.interfaces";
import WorkspaceFilter from "components/controls/workspace-filter/workspace-filter";
import { isWorkspaceFilterValid } from "lib/request-model-validation-helper";
import { IReportSectionProps } from "components/reports-common/report-section/report-section.props";
import ReportSection from "components/reports-common/report-section/report-section";
import {
    IFileSizeInTenantReportDetails,
    IFileSizeInTenantReportItem,
    IFileSizeInTenantReportRequestModel
} from "@interfaces/reports/file-size-reports.interfaces";
import FileSizeInTenantReportSummary from "components/reports/file-size-reports/file-size-in-tenant-report/file-size-in-tenant-report-summary";
import PersonalSiteFilter from "components/controls/personal-site-filter/personal-site-filter";
import DateTimeCell from "components/reports-common/report-results-grid-cells/date-time-cell/date-time-cell";
import LinkCell from "components/reports-common/report-results-grid-cells/link-cell/link-cell";
import { getSizeLabel } from "lib/utils";
import { FileSizeUnit, FilterOperator, FileSizeUnitType, FilterOperatorType } from "lib/enums";
import {
    Input,
    Label,
    makeStyles,
    Radio,
    RadioGroup,
    Select,
    Switch,
    tokens
} from "@fluentui/react-components";

const reportUrl: string = "/api/reports/file-size-in-tenant";

const layoutStyles = makeStyles({
    filterGroup: {
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        marginBottom: "16px"
    },
    filterRow: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "8px",
        '> *': {
            marginRight: '0'
        }
    },
    operatorGroup: {
        marginLeft: "48px",
        marginBottom: "4px"
    },
    numberInput: {
        width: "60px"
    },
    unitSelect: {
        minWidth: "70px"
    },
    disabledText: {
        color: tokens.colorNeutralForegroundDisabled
    }
});

export default function FileSizeInTenantReport() {
    const { t } = useL10n<ILocaleStrings>();
    const styles = layoutStyles();

    // State for filter criteria
    const [filterByVersionCount, setFilterByVersionCount] = useState<boolean>(false);
    const [minVersionCount, setMinVersionCount] = useState<number>(1);
    const [filterByFileSize, setFilterByFileSize] = useState<boolean>(false);
    const [minFileSize, setMinFileSize] = useState<number>(10);
    const [fileSizeUnit, setFileSizeUnit] = useState<FileSizeUnitType>(FileSizeUnit.MB);
    const [filterOperator, setFilterOperator] = useState<FilterOperatorType>(FilterOperator.And);
    const [personalSiteFilter, setPersonalSiteFilter] = useState<IPersonalSiteFilter>({
        includeWorkspaces: true,
        includeOneDrive: false
    });
    const [workspaceFilter, setWorkspaceFilter] = useState<IWorkspaceFilter>({
        applyFilter: false
    });

    const [model, setModel] = useState<IFileSizeInTenantReportRequestModel>({
        filterByVersionCount: false,
        minVersionCount: 1,
        filterByFileSize: false,
        minFileSize: 10,
        fileSizeUnit: FileSizeUnit.MB,
        filterOperator: FilterOperator.And,
        personalSiteFilter: {
            includeWorkspaces: true,
            includeOneDrive: false
        }
    });

    // Define table columns
    const cols: Array<ITableColumn<IFileSizeInTenantReportItem>> = [
        {
            key: "fileName",
            label: t("ppaFile"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 200,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                <LinkCell label={item.fileName} url={item.fileUrl} />
            )
        },
        {
            key: "fileUrl",
            label: t("ppaFileUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "folderPath",
            label: t("ppaFolder"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                <LinkCell label={item.folderPath} url={item.folderUrl} />
            )
        },
        {
            key: "folderUrl",
            label: t("ppaFolderUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "libraryName",
            label: t("ppaLibrary"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 120,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                <LinkCell label={item.libraryName} url={item.libraryUrl} />
            )
        },
        {
            key: "libraryUrl",
            label: t("ppaLibraryUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "workspaceName",
            label: t("ppaWorkspace"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                <LinkCell label={item.workspaceName} url={item.workspaceUrl} />
            )
        },
        {
            key: "workspaceUrl",
            label: t("ppaWorkspaceUrl"),
            showInGrid: false,
            showInExport: true
        },
        {
            key: "versionCount",
            label: t("ppaVersions"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 100
        },
        {
            key: "sizeKB",
            label: t("ppaSize"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 100,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                getSizeLabel(item.sizeKB, 'KB')
            ),
            getExportValue: (key: string, item: IFileSizeInTenantReportItem) => (
                getSizeLabel(item.sizeKB, 'KB')
            )
        },
        {
            key: "totalSizeKB",
            label: t("ppaTotalSize"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 100,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                getSizeLabel(item.totalSizeKB, 'KB')
            ),
            getExportValue: (key: string, item: IFileSizeInTenantReportItem) => (
                getSizeLabel(item.totalSizeKB, 'KB')
            )
        },
        {
            key: "lastModified",
            label: t("lastModified"),
            showInGrid: true,
            showInExport: true,
            defaultWidth: 150,
            renderCell: (key: string, item: IFileSizeInTenantReportItem) => (
                <DateTimeCell columnId={key} item={item} pattern="PPp" />
            )
        }
    ];

    // Update model when filter by version count changes
    const onFilterByVersionCountChange = (checked: boolean) => {
        setFilterByVersionCount(checked);
        setModel({
            ...model,
            filterByVersionCount: checked,
            minVersionCount: checked ? minVersionCount : undefined
        });
    };

    // Update model when min version count changes
    const onMinVersionCountChange = (value: string) => {
        const numValue = parseInt(value) || 1;
        setMinVersionCount(numValue);
        setModel({
            ...model,
            minVersionCount: filterByVersionCount ? numValue : undefined
        });
    };

    // Update model when filter by file size changes
    const onFilterByFileSizeChange = (checked: boolean) => {
        setFilterByFileSize(checked);
        setModel({
            ...model,
            filterByFileSize: checked,
            minFileSize: checked ? minFileSize : undefined,
            fileSizeUnit: checked ? fileSizeUnit : undefined
        });
    };

    // Update model when min file size changes
    const onMinFileSizeChange = (value: string) => {
        const numValue = parseFloat(value) || 1;
        setMinFileSize(numValue);
        setModel({
            ...model,
            minFileSize: filterByFileSize ? numValue : undefined
        });
    };

    // Update model when file size unit changes
    const onFileSizeUnitChange = (value: string) => {
        const unitValue = parseInt(value) as FileSizeUnitType;
        setFileSizeUnit(unitValue);
        setModel({
            ...model,
            fileSizeUnit: filterByFileSize ? unitValue : undefined
        });
    };

    // Update model when filter operator changes
    const onFilterOperatorChange = (value: string) => {
        const opValue = parseInt(value) as FilterOperatorType;
        setFilterOperator(opValue);
        setModel({
            ...model,
            filterOperator: opValue
        });
    };

    // Update model when personal site filter changes
    const onPersonalSiteFilterChange = (value?: IPersonalSiteFilter) => {
        setPersonalSiteFilter({
            includeWorkspaces: value?.includeWorkspaces ?? false,
            includeOneDrive: value?.includeOneDrive ?? false
        });
        setModel({
            ...model,
            personalSiteFilter: {
                includeWorkspaces: value?.includeWorkspaces,
                includeOneDrive: value?.includeOneDrive
            }
        });
    };

    // Update model when workspace filter changes
    const onWorkspaceFilterChange = (value?: IWorkspaceFilter) => {
        setWorkspaceFilter({
            applyFilter: value?.applyFilter || false,
            filter: value?.filter
        });
        setModel({
            ...model,
            workspaceFilter: value?.applyFilter ?
                {
                    property: value?.filter?.property,
                    operator: value?.filter?.operator,
                    value: value?.filter?.value
                }
                : undefined
        });
    };

    // Validation per design doc:
    // 1. At least one workspace type (Include Workspaces / Include OneDrives) must be selected
    // 2. Version count and file size filters are optional - neither is required
    // 3. But if a filter toggle is enabled, the corresponding input must have a valid value > 0
    const isModelValid = (): boolean => {
        // At least one personal site filter must be selected
        const hasPersonalSiteFilter = (model.personalSiteFilter?.includeWorkspaces || model.personalSiteFilter?.includeOneDrive) === true;

        // If version count filter is enabled, value must be valid (positive number > 0)
        const versionCountValid = !filterByVersionCount || (minVersionCount >= 1);

        // If file size filter is enabled, value must be valid (positive number > 0)
        const fileSizeValid = !filterByFileSize || (minFileSize > 0);

        // Workspace filter validation (if applied, must have all required fields)
        const workspaceFilterValid = isWorkspaceFilterValid(workspaceFilter.applyFilter, model.workspaceFilter);

        return hasPersonalSiteFilter && versionCountValid && fileSizeValid && workspaceFilterValid;
    };

    const reportSectionProps: IReportSectionProps<IFileSizeInTenantReportItem, IFileSizeInTenantReportDetails> = {
        columns: cols,
        summaryComponent: <FileSizeInTenantReportSummary />,
        reportUrl: reportUrl,
        model: model,
        isModelValid: isModelValid(),
        noItemsMessage: t("ppaFileSizeInTenantReportNoItemsMessage"),
        defaultExportFilenamePrefix: "ppaFileSizeInTenantReportDefaultExportFilenamePrefix"
    }

    return (
        <ReportSection {...reportSectionProps}>
            <div className={styles.filterGroup}>
                {/* Version count filter */}
                <div className={styles.filterRow}>
                    <Switch
                        checked={filterByVersionCount}
                        onChange={(_, data) => onFilterByVersionCountChange(data.checked)}
                        label={t("ppaMoreThan")}
                    />
                    <Input
                        appearance="underline"
                        type="number"
                        className={styles.numberInput}
                        value={minVersionCount.toString()}
                        onChange={(_, data) => onMinVersionCountChange(data.value)}
                        disabled={!filterByVersionCount}
                        min={1}
                    />
                    <Label className={!filterByVersionCount ? styles.disabledText : undefined}>
                        {t("ppaVersions")}
                    </Label>
                </div>

                {/* Filter operator (And/Or) */}
                <div className={styles.operatorGroup}>
                    <RadioGroup
                        value={filterOperator.toString()}
                        onChange={(_, data) => onFilterOperatorChange(data.value)}
                        disabled={!filterByVersionCount || !filterByFileSize}
                        layout="horizontal"
                    >
                        <Radio value={FilterOperator.And.toString()} label={t("ppaAnd")} />
                        <Radio value={FilterOperator.Or.toString()} label={t("ppaOr")} />
                    </RadioGroup>
                </div>

                {/* File size filter */}
                <div className={styles.filterRow}>
                    <Switch
                        checked={filterByFileSize}
                        onChange={(_, data) => onFilterByFileSizeChange(data.checked)}
                        label={t("ppaSizeLargerThan")}
                    />
                    <Input
                        appearance="underline"
                        type="number"
                        className={styles.numberInput}
                        value={minFileSize.toString()}
                        onChange={(_, data) => onMinFileSizeChange(data.value)}
                        disabled={!filterByFileSize}
                        min={1}
                    />
                    <Select
                        appearance="underline"
                        className={styles.unitSelect}
                        value={fileSizeUnit.toString()}
                        onChange={(_, data) => onFileSizeUnitChange(data.value)}
                        disabled={!filterByFileSize}
                    >
                        <option value={FileSizeUnit.MB.toString()}>MB</option>
                        <option value={FileSizeUnit.GB.toString()}>GB</option>
                    </Select>
                </div>
            </div>

            {/* Workspace filter (filters which sites to include) */}
            <WorkspaceFilter model={workspaceFilter} onChange={onWorkspaceFilterChange} />

            {/* Personal site filter (filters site types: Workspaces/OneDrives) */}
            <PersonalSiteFilter model={personalSiteFilter} onChange={onPersonalSiteFilterChange} />
        </ReportSection>
    );
}
